import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import Table from "@amzn/awsui-components-react-v3/polaris/table";
import Box from "@amzn/awsui-components-react-v3/polaris/box";
import SpaceBetween from "@amzn/awsui-components-react-v3/polaris/space-between";
import Button from "@amzn/awsui-components-react-v3/polaris/button";
import TextFilter from "@amzn/awsui-components-react-v3/polaris/text-filter";
import Header from "@amzn/awsui-components-react-v3/polaris/header";
import Pagination from "@amzn/awsui-components-react-v3/polaris/pagination";
import CollectionPreferences from "@amzn/awsui-components-react-v3/polaris/collection-preferences";
import StatusIndicator from "@amzn/awsui-components-react-v3/polaris/status-indicator";
import RadioGroup from "@amzn/awsui-components-react-v3/polaris/radio-group";
import { useSubscriptionItems } from "../../hooks/useSubscriptionItems";
import { useNavigate } from 'react-router-dom';
import ButtonDropdown from "@amzn/awsui-components-react-v3/polaris/button-dropdown";
import { useCollection } from '@amzn/awsui-collection-hooks';
import { useTranslation } from "react-i18next";
import { AwsAccountType, MAX_SUBSCRIPTION_TRAILING_12_MONTH_REGULAR_CUSTOMER, MAX_SUBSCRIPTION_TRAILING_12_MONTH_ATP_CUSTOMER, SessionKey, SubscriptionStatus, isAutoRenewalEnabled, SubscriptionRequestType, MAX_MIDTERM_SUBSCRIPTIONS_ALLOWED } from "../../common/constants";
import { UserContext } from "../../App";
import { useContext, useEffect, useState, useMemo } from "react";
import { triggerAnalyticsEvent } from "../../analytics/createEventTrigger";
import { addCommaToPrice, addDayToDate, addYearToDate, getSubscriptionRequestsOrderTotal, isRenewalUpdateWindowOpen } from "../../common/utilityFunctions";
import styled from 'styled-components';
import { NodeEnvironment, getNodeEnvironment } from "../../common/nodeEnvironment";
import { useHelpPanel, DashboardMainInfo } from '../../pages/SubscriptionManagementPage/help-panel';
import { InfoLink } from '../../pages/SubscriptionManagementPage/SubscriptionManagementPage';
import AwsAuthenticationModal from "../../hooks/useAwsAuthenticationModal";
const ItalicBox = styled.span `
  font-style: italic;
`;
const ChildRowContent = styled.div `
  padding-left: 40px; 
`;
const isNonProdEnvironment = () => {
    return getNodeEnvironment() !== NodeEnvironment.PROD;
};
export const transformSubscriptionData = (data) => {
    const parentMap = new Map();
    const parents = [];
    data.forEach(item => {
        if (!item.parentRequestId) {
            parents.push(item);
        }
        else {
            if (!parentMap.has(item.parentRequestId)) {
                parentMap.set(item.parentRequestId, []);
            }
            parentMap.get(item.parentRequestId).push(item);
        }
    });
    return parents.map(parent => ({
        ...parent,
        children: parentMap.get(parent.id) || []
    }));
};
const defaultContentDisplayPreferences = {
    contentDisplay: [
        // TO DO REMOVE FLAG - Add On
        ...(isNonProdEnvironment() ? [{ id: "selection", visible: true }] : []),
        { id: "submitted_by", visible: true },
        { id: "subscription_request_id", visible: true },
        { id: "order_date", visible: true },
        { id: "order_number", visible: true },
        { id: "subscription_status", visible: true },
        { id: "start_date", visible: true },
        { id: "end_date", visible: true },
        { id: "seats", visible: true },
        { id: "sign_in_method", visible: true },
        { id: "end_customer_aws_Account_id", visible: false },
        { id: "end_customer_name", visible: false },
        { id: "end_customer_country", visible: false },
        { id: "order_total", visible: true },
        { id: "billing_aws_account_id", visible: true },
    ],
    stickyColumns: {
        first: 0,
        last: 0
    },
    wrapLines: false,
    stripedRows: false,
    contentDensity: "comfortable",
    pageSize: 10
};
export const SubscriptionReqTable = () => {
    const [subscriptionCollection, loading, refetch] = useSubscriptionItems();
    const [awsAccountModalVisible, setAwsAccountModalVisible] = useState(false);
    const [spmsModalVisible, setSpmsModalVisible] = useState(false);
    const [contentDisplayPreferences, setContentDisplayPreferences] = useState(defaultContentDisplayPreferences);
    const [regularSubscriptionLimitReached, setRegularSubscriptionLimitReached] = useState(false);
    const [atpSubscriptionLimitReached, setAtpSubscriptionLimitReached] = useState(false);
    const [regularSubscriptionEnablementDate, setRegularSubscriptionEnablementDate] = useState();
    const [atpSubscriptionEnablementDate, setAtpSubscriptionEnablementDate] = useState();
    const [expandedItems, setExpandedItems] = useState([]);
    const [selectedValue, setSelectedValue] = useState("");
    const [awsModalOptions, setAwsModalOptions] = useState({
        visible: false
    });
    const VALID_RENEWAL_STATUSES = [SubscriptionStatus.ACTIVE, SubscriptionStatus.REQUEST_SUBMITTED, SubscriptionStatus.ONBOARDING_IN_PROCESS];
    const VALID_ADD_SEAT_STATUSES = [SubscriptionStatus.ACTIVE];
    const loadHelpPanelContentInfo = useHelpPanel();
    const getVisibleItems = (items) => {
        const visibleItems = [];
        items.forEach(item => {
            visibleItems.push(item);
            if (expandedItems.some(expanded => expanded.id === item.id)) {
                visibleItems.push(...(item.children || []));
            }
        });
        return visibleItems;
    };
    const user = useContext(UserContext);
    const navigate = useNavigate();
    const { t } = useTranslation();
    const transformedData = useMemo(() => {
        return transformSubscriptionData(subscriptionCollection || []);
    }, [subscriptionCollection]);
    // TO DO REMOVE FLAG - Add On
    const { items, actions, filteredItemsCount, collectionProps, filterProps, paginationProps } = useCollection(isNonProdEnvironment() ? (transformedData || []) : (subscriptionCollection || []), {
        filtering: {
            empty: (_jsx(Box, { children: "No matches" })),
            noMatch: (_jsx(Box, { children: "No matches" })),
            filteringFunction: (item, filteringText, filteringFields) => {
                if (!filteringText) {
                    return true;
                }
                const machingRow = Object.values(item).find((value) => {
                    if (typeof value === 'string' && value.toLowerCase().includes(filteringText.toLowerCase())) {
                        return true;
                    }
                });
                return !!machingRow;
            }
        },
        selection: {},
        sorting: {},
        pagination: {
            pageSize: contentDisplayPreferences.pageSize
        },
    });
    const PendingBox = () => {
        return _jsx(ItalicBox, { children: _jsx(Box, { color: "text-status-inactive", variant: "span", children: t("pending_text").toLowerCase() }) });
    };
    useEffect(() => {
        refetch(); // Refetch the query when the component mounts
    }, []);
    useEffect(() => {
        const getDateOneYearAgo = () => {
            return new Date(new Date().setFullYear(new Date().getFullYear() - 1));
        };
        if (!loading) {
            const oneYearAgoDate = getDateOneYearAgo();
            // Get all regular subscription requests in last 12 months
            const regularSubscriptionRequests = items.filter((item) => !item.partnerRequest);
            const regularSubscriptionRequestsInLastYear = regularSubscriptionRequests.filter((item) => {
                return new Date(item.createdAt) > oneYearAgoDate;
            });
            // Get all partner subscription requests in last 12 months
            const atpSubscriptionRequests = items.filter((item) => item.partnerRequest);
            const atpSubscriptionRequestsInLastYear = atpSubscriptionRequests.filter((item) => {
                return new Date(item.createdAt) > oneYearAgoDate;
            });
            // If more than 5 regular subscription requests in last 12 months, then set max limit reached to true
            if (regularSubscriptionRequestsInLastYear && regularSubscriptionRequestsInLastYear.length >= MAX_SUBSCRIPTION_TRAILING_12_MONTH_REGULAR_CUSTOMER) {
                setRegularSubscriptionLimitReached(true);
                // Get date of oldest sub
                regularSubscriptionRequestsInLastYear.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
                const oldestSubscriptionDateInLast12Month = new Date(regularSubscriptionRequestsInLastYear[0].createdAt);
                // Add 1 year and 1 day to oldest sub date
                setRegularSubscriptionEnablementDate(addDayToDate(addYearToDate(oldestSubscriptionDateInLast12Month, 1), 1).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }));
            }
            // If more than 500 partner subscription requests in last 12 months, then set max limit reached to true
            if (atpSubscriptionRequestsInLastYear && atpSubscriptionRequestsInLastYear.length >= MAX_SUBSCRIPTION_TRAILING_12_MONTH_ATP_CUSTOMER) {
                setAtpSubscriptionLimitReached(true);
                // Get date of oldest sub
                atpSubscriptionRequestsInLastYear.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
                const oldestSubscriptionDateInLast12Month = new Date(atpSubscriptionRequestsInLastYear[0].createdAt);
                // Add 1 year and 1 day to oldest sub date
                setAtpSubscriptionEnablementDate(addDayToDate(addYearToDate(oldestSubscriptionDateInLast12Month, 1), 1).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }));
            }
        }
    }, [loading]);
    useEffect(() => {
        if (!loading && subscriptionCollection?.length > 0) {
            const hasPartnerRequests = subscriptionCollection.some(item => item.partnerRequest);
            setContentDisplayPreferences(prev => {
                const partnerColumns = [
                    "end_customer_aws_Account_id",
                    "end_customer_name",
                    "end_customer_country"
                ];
                const currentVisibilities = partnerColumns.map(columnId => prev.contentDisplay.find(d => d.id === columnId)?.visible);
                if (currentVisibilities.some(visibility => visibility !== hasPartnerRequests)) {
                    return {
                        ...prev,
                        contentDisplay: prev.contentDisplay.map(display => partnerColumns.includes(display.id)
                            ? { ...display, visible: hasPartnerRequests }
                            : display)
                    };
                }
                return prev;
            });
        }
    }, [loading, subscriptionCollection]);
    const handleStartNewSubscriptionButtonClick = (accountType) => {
        sessionStorage.setItem(SessionKey.AWS_ACCOUNT_TYPE, accountType);
        sessionStorage.setItem(SessionKey.REQUEST_TYPE, 'newSubscriptionRequest');
        if (accountType === AwsAccountType.PARTNER) {
            sessionStorage.setItem(SessionKey.IS_AMAZON_TRAINING_PARTNER, "true");
            setAwsAccountModalVisible(true);
            setAwsModalOptions({
                visible: true
            });
            triggerAnalyticsEvent('click', `SBTS_${getNodeEnvironment()} | subscription_management | new_subscription_partner | event_started`);
        }
        else {
            sessionStorage.removeItem(SessionKey.IS_AMAZON_TRAINING_PARTNER);
            setAwsAccountModalVisible(true);
            setAwsModalOptions({
                visible: true
            });
            triggerAnalyticsEvent('click', `SBTS_${getNodeEnvironment()} | subscription_management | new_subscription_regular | event_started`);
        }
    };
    // For the "Add Seats" flow:
    const handleAddSeatsClick = (selectedItem) => {
        if (selectedItem?.status === SubscriptionStatus.ACTIVE) {
            const numberOfParentSeats = selectedItem.numberOfSeats;
            const midTermRequests = subscriptionCollection.filter(item => item.parentRequestId === selectedItem.id &&
                item.subscriptionRequestType === SubscriptionRequestType.MIDTERM);
            const totalNumberOfMidtermSeats = midTermRequests.reduce((total, child) => total + child.numberOfSeats, 0);
            const totalNumberOfSeats = numberOfParentSeats + totalNumberOfMidtermSeats;
            sessionStorage.setItem(SessionKey.TOTAL_EXISTING_SEATS, totalNumberOfSeats.toString());
            setAwsAccountModalVisible(true);
            setAwsModalOptions({
                visible: true
            });
            sessionStorage.setItem(SessionKey.REQUEST_TYPE, 'midTermAddonRequest');
            sessionStorage.setItem(SessionKey.PARENT_SUBSCRIPTION_REQUEST_ID, selectedItem.id);
            triggerAnalyticsEvent('click', `SBTS_${getNodeEnvironment()} | subscription_management | add_seats | modal_opened`);
        }
    };
    const handleUpdateRenewalPreferenceClick = (selectedItem) => {
        setAwsAccountModalVisible(true);
        setAwsModalOptions({
            visible: true
        });
        sessionStorage.setItem(SessionKey.REQUEST_TYPE, 'subscriptionRenewalRequest');
        sessionStorage.setItem(SessionKey.SUBSCRIPTION_REQUEST_ID, selectedItem.id);
        triggerAnalyticsEvent('click', `SBTS_${getNodeEnvironment()} | subscription_management | update_renewal_preferences | modal_opened`);
    };
    const canAddSeats = (selectedItems) => {
        if (!selectedItems || selectedItems.length === 0)
            return false;
        const selectedItem = selectedItems[0];
        // First check if status is valid
        if (!VALID_ADD_SEAT_STATUSES.includes(selectedItem?.status))
            return false;
        // Count child requests (midterm add-ons) for this parent
        const midTermOrderCount = subscriptionCollection.filter(item => item.parentRequestId === selectedItem.id &&
            item.subscriptionRequestType === SubscriptionRequestType.MIDTERM).length;
        // Allow add seats only if there are less than 3 child requests
        return midTermOrderCount < MAX_MIDTERM_SUBSCRIPTIONS_ALLOWED;
    };
    const canUpdateRenewalPreferences = (selectedItems) => {
        if (!selectedItems || selectedItems.length === 0)
            return false;
        const selectedItem = selectedItems[0];
        return isAutoRenewalEnabled() &&
            VALID_RENEWAL_STATUSES.includes(selectedItem?.status) &&
            isRenewalUpdateWindowOpen(new Date(selectedItem.createdAt));
    };
    const getPrettySubscriptionStatus = (status) => {
        switch (status) {
            case SubscriptionStatus.REQUEST_SUBMITTED:
                return {
                    text: t('subscription_request_status_request_submitted'),
                    type: 'in-progress'
                };
            case SubscriptionStatus.ONBOARDING_IN_PROCESS:
                return {
                    text: t('subscription_request_status_onboarding_in_process'),
                    type: 'in-progress'
                };
            case SubscriptionStatus.ACTIVE:
                return {
                    text: t('subscription_request_status_active'),
                    type: 'success'
                };
            case SubscriptionStatus.CANCELLATION_IN_PROGRESS:
                return {
                    text: t('subscription_request_status_cancellation_in_progress'),
                    type: 'in-progress'
                };
            default:
                return {
                    text: status,
                    type: 'in-progress'
                };
        }
    };
    const ButtonDropdownWithDescription = ({ items, ...props }) => {
        const itemsWithDescription = items.map(item => ({
            ...item,
            text: (_jsxs("div", { children: [_jsx("div", { children: item.text }), item.description && (_jsx("div", { style: {
                            color: 'black',
                            marginTop: '0.3em'
                        }, children: item.description }))] }))
        }));
        return (_jsx(ButtonDropdown, { ...props, items: itemsWithDescription }));
    };
    return (_jsxs(_Fragment, { children: [_jsx(Table, { ...collectionProps, renderAriaLive: ({ firstIndex, lastIndex, totalItemsCount }) => t("subscription_request_table_aria_description", { firstIndex, lastIndex, totalItemsCount }), totalItemsCount: subscriptionCollection?.length || 0, columnDefinitions: [
                    // TO DO REMOVE FLAG - Add On
                    ...(isNonProdEnvironment() ? [{
                            id: "selection",
                            header: "",
                            cell: (item) => !item.parentRequestId && (_jsx(RadioGroup, { onChange: ({ detail }) => {
                                    setSelectedValue(detail.value);
                                    actions.setSelectedItems([item]);
                                }, value: selectedValue, items: [
                                    {
                                        value: item.id,
                                        label: "",
                                        disabled: false
                                    }
                                ] })),
                            width: 50
                        }] : []),
                    {
                        id: "id",
                        header: "ID",
                        cell: (item) => item.id,
                        sortingField: "id"
                    },
                    {
                        id: "submitted_by",
                        header: `${t("subscription_request_column_header_submitted_by")}`,
                        cell: (item) => {
                            // TO DO REMOVE FLAG - Add On
                            if (!isNonProdEnvironment()) {
                                return `${item.firstName} ${item.lastName}`;
                            }
                            else {
                                // Beta or Gamma
                                if (item.children?.length > 0) {
                                    return (_jsxs(_Fragment, { children: [_jsx(Button, { variant: "icon", iconName: expandedItems.some(expanded => expanded.id === item.id) ? "caret-down-filled" : "caret-right-filled", "data-testid": `expand-button-${item.id}`, onClick: () => {
                                                    setExpandedItems(prev => {
                                                        const next = new Set(prev.map(item => item.id));
                                                        const isExpanded = next.has(item.id);
                                                        if (isExpanded) {
                                                            next.delete(item.id);
                                                        }
                                                        else {
                                                            next.add(item.id);
                                                        }
                                                        return [...next].map(id => ({ id }));
                                                    });
                                                } }), _jsx("span", { children: `${item.firstName} ${item.lastName}` })] }));
                                }
                                if (item.parentRequestId) {
                                    return (_jsx(ChildRowContent, { children: _jsx("span", { children: `${item.firstName} ${item.lastName}` }) }));
                                }
                                return _jsx("span", { children: `${item.firstName} ${item.lastName}` });
                            }
                        },
                        sortingField: "firstName",
                    },
                    {
                        id: "subscription_request_id",
                        header: `${t("subscription_request_column_header_subscription_request_id")}`,
                        cell: (item) => `${item.id}`,
                        sortingField: "id",
                    },
                    {
                        id: "order_date",
                        header: `${t("subscription_request_column_header_request_date")}`,
                        cell: (item) => new Date(item.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }),
                        sortingField: "createdAt",
                    },
                    {
                        id: "order_number",
                        header: `${t("subscription_request_column_header_order_number")}`,
                        cell: (item) => item.orderId || _jsx(PendingBox, {}),
                        sortingField: "order_number"
                    },
                    {
                        id: "subscription_status",
                        header: `${t("subscription_request_column_header_subscription_status")}`,
                        cell: item => {
                            const status = getPrettySubscriptionStatus(item.status);
                            return (_jsx(StatusIndicator, { type: status.type, children: status.text }));
                        },
                        sortingField: "status"
                    },
                    {
                        id: "start_date",
                        header: `${t("subscription_request_column_header_start_date")}`,
                        cell: item => item.subscriptionStartDate ? new Date(item.subscriptionStartDate).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' }) : _jsx(PendingBox, {}),
                        sortingField: "subscriptionStartDate"
                    },
                    {
                        id: "end_date",
                        header: `${t("subscription_request_column_header_end_date")}`,
                        cell: item => item.subscriptionEndDate ? new Date(item.subscriptionEndDate).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' }) : _jsx(PendingBox, {}),
                        sortingField: "subscriptionEndDate"
                    },
                    {
                        id: "seats",
                        header: `${t("subscription_request_column_header_seats")}`,
                        cell: item => item.numberOfSeats,
                        sortingField: "numberOfSeats"
                    },
                    {
                        id: "sign_in_method",
                        header: `${t("subscription_request_column_header_sign_in_method")}`,
                        cell: item => item.signInMethod,
                        sortingField: "signInMethod"
                    },
                    {
                        id: "end_customer_aws_Account_id",
                        header: `${t("subscription_request_column_header_end_customer_account_id")}`,
                        cell: item => item.partnerRequest ? (item.awsEndUserAccount ?? _jsx(Box, { textAlign: "center", children: "---" })) : null,
                        sortingField: "awsEndUserAccount"
                    },
                    {
                        id: "end_customer_name",
                        header: `${t("subscription_request_column_header_end_customer_name")}`,
                        cell: item => item.partnerRequest ? item.endCustomerCompanyName : null,
                        sortingField: "endCustomerCompanyName"
                    },
                    {
                        id: "end_customer_country",
                        header: `${t("subscription_request_column_header_end_customer_country")}`,
                        cell: item => item.partnerRequest ? item.endCustomerCountry : null,
                        sortingField: "endCustomerCountry"
                    },
                    {
                        id: "order_total",
                        header: `${t("subscription_request_column_header_order_total")}`,
                        cell: item => {
                            const orderTotal = getSubscriptionRequestsOrderTotal(item);
                            return orderTotal ? addCommaToPrice(orderTotal) : _jsx(PendingBox, {});
                        },
                        sortingField: "order_total"
                    },
                    {
                        id: "billing_aws_account_id",
                        header: `${t("subscription_request_column_header_billing_aws_account_id")}`,
                        cell: item => item.linkedAccountId,
                        sortingField: "linkedAccountId"
                    }
                ], columnDisplay: contentDisplayPreferences.contentDisplay, enableKeyboardNavigation: true, 
                // TO DO REMOVE FLAG - Add On
                items: isNonProdEnvironment() ? getVisibleItems(items) : items, loadingText: t("subscription_request_table_loading_text"), loading: loading, trackBy: "id", empty: _jsx(Box, { margin: { vertical: "xs" }, textAlign: "center", color: "inherit", children: _jsxs(SpaceBetween, { size: "s", children: [_jsx("b", { children: t("subscription_request_no_subscriptions_text") }), _jsx(Box, { variant: "p", children: t("subscription_request_no_subscriptions_description") }), _jsx(ButtonDropdownWithDescription, { onItemClick: ({ detail }) => {
                                    handleStartNewSubscriptionButtonClick(detail.id);
                                }, items: [
                                    { text: `${t('subscription_request_regular_customer_option_text_new')}`, id: "regular", disabled: false },
                                    { text: `${t('subscription_request_partner_customer_option_text_latest')}`, description: `${t('subscription_request_partner_customer_description_latest')}`,
                                        id: "partner", disabled: false },
                                ], variant: "normal", children: t("subscription_request_start_new_subscription_button_text_latest") })] }) }), header: 
                // TO DO REMOVE FLAG - Add On
                _jsx(Header, { counter: `(${isNonProdEnvironment() ? filteredItemsCount : (subscriptionCollection?.length || 0)})`, actions: _jsxs(SpaceBetween, { direction: "horizontal", size: "xs", children: [isNonProdEnvironment() && (_jsx(ButtonDropdown, { disabled: collectionProps.selectedItems?.length === 0 || collectionProps.selectedItems?.[0]?.status === "CANCELLATION_IN_PROGRESS", items: [
                                    {
                                        id: 'addSeats',
                                        text: 'Add Seats',
                                        disabled: !canAddSeats(collectionProps.selectedItems)
                                    },
                                    {
                                        id: 'updateRenewalPreferences',
                                        text: 'Update Renewal Preferences',
                                        disabled: !canUpdateRenewalPreferences(collectionProps.selectedItems)
                                    }
                                ], onItemClick: ({ detail }) => {
                                    if (detail.id === 'addSeats') {
                                        handleAddSeatsClick(collectionProps.selectedItems?.[0]);
                                    }
                                    else if (detail.id === 'updateRenewalPreferences') {
                                        handleUpdateRenewalPreferenceClick(collectionProps.selectedItems?.[0]);
                                    }
                                }, children: t('subscription_request_modify_button') })), _jsx(ButtonDropdownWithDescription, { onItemClick: ({ detail }) => {
                                    handleStartNewSubscriptionButtonClick(detail.id);
                                }, items: [
                                    {
                                        text: `${t('subscription_request_regular_customer_option_text_new')}`,
                                        id: "regular",
                                        disabled: regularSubscriptionLimitReached,
                                        disabledReason: `You have already submitted 5 subscription request within past 12 months. You can place new request on or after ${regularSubscriptionEnablementDate}`
                                    },
                                    {
                                        text: `${t('subscription_request_partner_customer_option_text_latest')}`,
                                        description: `${t('subscription_request_partner_customer_description_latest')}`,
                                        id: "partner",
                                        disabled: atpSubscriptionLimitReached,
                                        disabledReason: `You have already submitted 500 subscription request within past 12 months. You can place new request on or after ${atpSubscriptionEnablementDate}`
                                    },
                                ], variant: "primary", children: t("subscription_request_start_new_subscription_button_text_latest") })] }), info: _jsx(InfoLink, { onFollow: () => {
                            loadHelpPanelContentInfo(_jsx(DashboardMainInfo, {}));
                        } }), children: t("subscription_request_table_header") }), filter: _jsx(TextFilter, { ...filterProps, filteringPlaceholder: t("subscription_request_filter_placeholder_text"), countText: `${filteredItemsCount} ${filteredItemsCount === 1 ? 'match' : 'matches'}` }), stickyColumns: contentDisplayPreferences.stickyColumns, contentDensity: contentDisplayPreferences.contentDensity, wrapLines: contentDisplayPreferences.wrapLines, stripedRows: contentDisplayPreferences.stripedRows, sortingColumn: collectionProps.sortingColumn, onSortingChange: ({ detail }) => actions.setSorting(detail), pagination: _jsx(Pagination, { ...paginationProps, disabled: loading }), preferences: _jsx(CollectionPreferences, { title: t("preferences_text"), confirmLabel: t("confirm_text"), cancelLabel: t("cancel_text"), onConfirm: ({ detail }) => {
                        setContentDisplayPreferences(detail);
                    }, preferences: contentDisplayPreferences, pageSizePreference: {
                        title: t("subscription_request_table_page_size_header"),
                        options: [
                            { value: 10, label: "10 resources" },
                            { value: 20, label: "20 resources" }
                        ]
                    }, wrapLinesPreference: {
                        label: t('subscription_request_table_wrap_lines_preference_label'),
                        description: t('subscription_request_table_wrap_lines_preference_description')
                    }, stripedRowsPreference: {
                        label: t('subscription_request_table_striped_rows_preference_label'),
                        description: t('subscription_request_table_striped_rows_preference_description')
                    }, contentDensityPreference: {
                        label: t('subscription_request_table_content_density_preference_label'),
                        description: t('subscription_request_table_content_density_preference_description')
                    }, contentDisplayPreference: {
                        title: t("subscription_request_table_column_preferences_header"),
                        description: t("subscription_request_table_column_preferences_description"),
                        options: [
                            {
                                id: "submitted_by",
                                label: t("subscription_request_column_header_submitted_by"),
                                alwaysVisible: true
                            },
                            {
                                id: "subscription_request_id",
                                label: t("subscription_request_column_header_subscription_request_id"),
                                alwaysVisible: true
                            },
                            {
                                id: "order_date",
                                label: t("subscription_request_column_header_request_date"),
                                alwaysVisible: true
                            },
                            {
                                id: "order_number",
                                label: t("subscription_request_column_header_order_number")
                            },
                            {
                                id: "subscription_status",
                                label: t("subscription_request_column_header_subscription_status"),
                                alwaysVisible: true
                            },
                            {
                                id: "start_date",
                                label: t("subscription_request_column_header_start_date"),
                                alwaysVisible: true
                            },
                            {
                                id: "end_date",
                                label: t("subscription_request_column_header_end_date"),
                                alwaysVisible: true
                            },
                            {
                                id: "seats",
                                label: t("subscription_request_column_header_seats"),
                                alwaysVisible: true
                            },
                            {
                                id: "sign_in_method",
                                label: t("subscription_request_column_header_sign_in_method"),
                                alwaysVisible: true
                            },
                            { id: "end_customer_aws_Account_id", label: t("subscription_request_column_header_end_customer_account_id") },
                            { id: "end_customer_name", label: t("subscription_request_column_header_end_customer_name") },
                            { id: "end_customer_country", label: t("subscription_request_column_header_end_customer_country") },
                            { id: "order_total", label: t("subscription_request_column_header_order_total") },
                            { id: "billing_aws_account_id", label: t("subscription_request_column_header_billing_aws_account_id") },
                        ]
                    }, stickyColumnsPreference: {
                        firstColumns: {
                            title: t('subscription_request_table_sticky_columns_preference_label'),
                            description: t('subscription_request_table_sticky_columns_preference_description'),
                            options: [
                                { label: t('none_text'), value: 0 },
                                { label: t('subscription_request_table_first_column_text'), value: 1 },
                                { label: t('subscription_request_table_first_two_columns_text'), value: 2 }
                            ]
                        },
                        lastColumns: {
                            title: t('subscription_request_table_sticky_last_columns_preference_label'),
                            description: t('subscription_request_table_sticky_last_columns_preference_description'),
                            options: [
                                { label: t('none_text'), value: 0 },
                                { label: t('subscription_request_table_last_column_text'), value: 1 }
                            ]
                        }
                    } }) }), awsAccountModalVisible && _jsx(AwsAuthenticationModal, { visible: awsModalOptions.visible, onDismiss: () => setAwsModalOptions(prev => ({ ...prev, visible: false })), userId: user.id })] }));
};
